import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import type { TeamRepoScore } from '@sdd-maturity/shared-types';
import type { ScoreMode } from '../contexts/ScoreModeContext';

const DIMENSION_LABELS: Record<string, string> = {
  'constitution-maturity': 'Constitution Maturity',
  'artifact-traceability': 'Artifact Traceability',
  'supplemental-context': 'Supplemental Context',
  'feedback-loop': 'Feedback Loop',
  'ai-collaboration': 'AI Collaboration',
  'framework-detection': 'Process Consistency',
};

interface StrengthRow {
  dimension: string;
  description: string;
  count: number;
}

/** Roll repo-level dimension strengths up to recurring team strengths (most-common first). */
function aggregateStrengths(repos: readonly TeamRepoScore[]): StrengthRow[] {
  const byKey = new Map<string, StrengthRow>();
  for (const repo of repos) {
    for (const dim of repo.dimensions ?? []) {
      for (const s of dim.strengths ?? []) {
        const key = `${dim.dimension}::${s.description}`;
        const existing = byKey.get(key);
        if (existing) existing.count += 1;
        else byKey.set(key, { dimension: dim.dimension, description: s.description, count: 1 });
      }
    }
  }
  return [...byKey.values()].sort((a, b) => b.count - a.count);
}

export interface StrengthsPanelProps {
  mode: ScoreMode;
  /** Delivery repos — required to aggregate Delivery strengths. */
  repos?: readonly TeamRepoScore[];
}

/**
 * FR-4 — "What's working" recurring strengths, surfaced symmetrically with gaps
 * but visually distinct (check icon + phase color, never severity red). Delivery reuses
 * the per-repo `dimensions[].strengths[]` aggregation. Discovery has no strengths
 * data source yet (Bedrock emits gaps only), so the section is gated/hidden until
 * one exists — see FOR-ENGINEERING.md.
 */
export function StrengthsPanel({ mode, repos = [] }: StrengthsPanelProps): React.JSX.Element | null {
  // Discovery strengths: gated until a data source exists.
  if (mode === 'discovery') return null;

  const rows = aggregateStrengths(repos);
  if (rows.length === 0) return null;

  const accent = 'var(--genai-sem-success)';

  return (
    <section data-testid="strengths-panel" className="mb-lg">
      <h3 className="text-xl font-bold mb-xs flex items-center gap-sm" style={{ color: accent }}>
        <CheckIcon aria-hidden="true" style={{ width: 18, height: 18, flexShrink: 0 }} />
        What's working — recurring strengths
      </h3>
      <p className="text-secondary mb-md" style={{ marginTop: 0 }}>
        Patterns this team is doing well — keep and spread these.
      </p>
      <div className="card">
        <div className="flex flex-col gap-sm">
          {rows.map((row) => (
            <div
              key={`${row.dimension}-${row.description}`}
              data-testid="strength-row"
              className="flex items-start gap-sm"
              style={{ padding: 'var(--genai-space-xs) 0' }}
            >
              <CheckIcon aria-hidden="true" style={{ width: 16, height: 16, color: accent, flexShrink: 0 }} />
              <div>
                <span className="badge badge-success" style={{ marginRight: 'var(--genai-space-sm)' }}>
                  {DIMENSION_LABELS[row.dimension] ?? row.dimension}
                </span>
                <span className="text-base text-primary">{row.description}</span>
                {row.count > 1 && (
                  <span className="text-xs text-muted" style={{ marginLeft: 'var(--genai-space-sm)' }}>
                    · {row.count} repos
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
